import { Component, HostBinding, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DxFormComponent, DxFormModule } from 'devextreme-angular';
import { EditorEnterKeyEvent } from 'devextreme/ui/form';
import { Subscription } from 'rxjs';
import { AppAngularBridge } from '~/app/app-bridge';
import { ResetPasswordService } from '~/app/core/auth/reset-password/reset-password.service';
import { MessageService, TOASTTYPE } from '~/app/core/services/message.service';
import { LayoutService } from '~/app/core/services/store/layout.service';
import { KeyboardNavigableComponent } from '~/app/shared/components/keyboard-navigable.component';
import { ReloadOnLangChangeDirective } from '~/app/shared/directives/reload-on-lang-change.directive';

import { AppButtonComponent } from '~/app/shared/components/app-button.component';
import { AuthService } from '~/app/core/auth/services/authentication.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  imports: [ReloadOnLangChangeDirective, DxFormModule, TranslateModule, AppButtonComponent],
})
export class ResetPasswordComponent extends KeyboardNavigableComponent implements OnInit, OnDestroy {
  @ViewChild(DxFormComponent) public form?: DxFormComponent;

  @HostBinding('class.right-menu') public addClass = false;

  public session: string | null = null;
  public disableKeyBoardFix = false;

  private layoutSubscription?: Subscription;

  public authService = inject(AuthService);
  private resetPasswordService = inject(ResetPasswordService);
  private messageService = inject(MessageService);
  private activatedRoute = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  private layoutService = inject(LayoutService);

  protected get editorOptions() {
    return {
      mode: 'email',
      disabled: this.disableKeyBoardFix,
    };
  }

  protected get passwordEditorOptions() {
    return {
      mode: 'password',
      disabled: this.disableKeyBoardFix,
    };
  }

  public ngOnInit() {
    this.addClass = true;
    this.getSession();

    if (AppAngularBridge.isNative()) {
      this.layoutSubscription = this.layoutService.showUserSidebarSubject.subscribe((isOpen) => {
        if (isOpen) {
          this.disableKeyBoardFix = true;
        }
        AppAngularBridge.sideBarStatus(isOpen).subscribe({
          next: () => {
            if (isOpen) {
              this.disableKeyBoardFix = false;
            }
          },
          error: () => {
            this.disableKeyBoardFix = false;
          },
        });
      });
    }
  }

  public ngOnDestroy() {
    this.layoutSubscription?.unsubscribe();
  }

  public passwordComparison = () => {
    return this.form?.instance.option('formData').newPassword;
  };

  public requestSession(event?: EditorEnterKeyEvent) {
    if (!this.form) return;

    if (this.form.instance.validate().isValid) {
      this.blurAllFields(event);
      this.resetPasswordService.requestSession(this.form.instance.option('formData').email).subscribe(() => {
        this.messageService.showToast(TOASTTYPE.SUCCESS, this.translate.instant('passwordReset.mailSend'));
        this.form?.instance.clear();
      });
    } else {
      if (!event) return;
      this.focusNextField(event, this.form.instance);
    }
  }

  public finalizeSession(event?: EditorEnterKeyEvent) {
    if (!this.form) return;

    if (this.form.instance.validate().isValid && this.session) {
      this.blurAllFields(event);
      this.resetPasswordService
        .finalizeSession(this.form.instance.option('formData').newPassword, this.session)
        .subscribe(() => {
          this.messageService.showToast(TOASTTYPE.SUCCESS, this.translate.instant('passwordReset.newPwSet'));
          this.form?.instance.clear();
          this.authService.confirmed = true;
          this.authService.resetPasswordUser = false;
        });
    } else {
      if (!event) return;
      this.focusNextField(event, this.form.instance);
    }
  }

  private getSession() {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.session = params['s'];
    });
  }
}
